import React, { useEffect, useState } from "react";
import { useParams, useSearchParams  } from "react-router-dom";
import { apiFetch } from "../../api/api";
import { AxiosError } from "axios";

interface Props {
    formId?: number | string;
    endpoint?: string;
}

const Questionnaire: React.FC<Props> = ({ formId: propFormId, endpoint }) => {
    const params = useParams<{ form_id: string }>();
    const form_id = propFormId ?? params.form_id ?? null;

    const [searchParams] = useSearchParams();
    const sidParam = searchParams.get("sid");
    const sid = sidParam ? Number(sidParam) : 0;

    const [question, setQuestion] = useState<any | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [finished, setFinished] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [selectedOptIds, setSelectedOptIds] = useState<(number | string)[]>([]);
    const [textAnswer, setTextAnswer] = useState<string>("");

    const fetchQuestion = async (qid: number | null = null, oid: number | string | null = null) => {
        if (form_id == null) return;
        setLoading(true);
        setError(null);

        try {
            const res = await apiFetch({
                data: { params: [null, 5, null, "0915,100,1;0939,250,326598;0963256598,102,659744;", null, 1, 5, null]
                   //  params: [Number(form_id), qid, oid, sid, 10, null] // asli
                    // params: [null,Number(form_id), null, 1123, 1, 6]
                },

            });
            const data = res ?? {};
            if (data?.success && Array.isArray(data.rows) && data.rows.length > 0) {
                const q = data.rows[0];
                if (typeof q.Opt === "string") {
                    try {
                        q.Opt = JSON.parse(q.Opt);
                    } catch (e) {
                        q.Opt = [];
                    }
                }
                setQuestion(q);
                setSelectedOptIds([]);
                setTextAnswer("");
                setFinished(false);
            } else {
                setQuestion(null);
                setFinished(true);
            }
        } catch (e) {
            const axiosError = e as AxiosError;
            console.error("خطا در دریافت سوال:", axiosError?.message ?? e);
            setError(axiosError?.message ?? "خطا در ارتباط با سرور");
            setQuestion(null);
            setFinished(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (form_id != null) fetchQuestion();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form_id]);

    const isMulti = question?.QType === 3;
    const isText = question?.QType === 2;

    const toggleOption = (optId: number | string) => {
        setSelectedOptIds((prev) => {
            const exists = prev.includes(optId);
            if (exists) return prev.filter((id) => id !== optId);
            return [...prev, optId];
        });
    };

    const handleAnswer = (optId: number | string) => {
        if (!question) return;
        if (isMulti) {
            toggleOption(optId);
        } else if (!isText) {
            // QType=1
            fetchQuestion(question.QID ?? null, Number(optId));
        }
    };

    const handleNext = () => {
        if (!question) return;

        if (isMulti) {
            const oidStr = selectedOptIds.join(",");
            fetchQuestion(question.QID ?? null, oidStr);
        } else if (isText) {
            fetchQuestion(question.QID ?? null, textAnswer);
        }
    };

    return (
        <div
            dir="rtl"
            className="flex items-center justify-center min-h-screen bg-center bg-cover"
            style={{
                backgroundImage: `url("/hospital.jpeg")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "blur(0px)",
            }}
        >
            <div className="text-center text-gray-800 backdrop-blur-[2px]">
                {loading ? (
                    <div className="text-lg text-gray-700">در حال بارگذاری سوال...</div>
                ) : error ? (
                    <div className="text-red-600 text-lg font-medium">خطا: {error}</div>
                ) : finished ? (
                    <div className="text-gray-800 text-lg font-semibold">
                        شما به تمام سوالات پاسخ داده‌اید.
                    </div>
                ) : question ? (
                    <div>
                        <h2 className="text-lg md:text-xl font-semibold mb-10 font-public">
                            {question.Title ?? "پایان پرسشنامه"}
                        </h2>

                        {/* QType=2 تشریحی */}
                        {isText ? (
                            <div className="w-full max-w-xl mx-auto">
                <textarea
                    value={textAnswer}
                    onChange={(e) => setTextAnswer(e.target.value)}
                    rows={5}
                    className="w-full p-3 rounded-md border border-blue-900 bg-white/70 text-gray-800 font-public"
                    placeholder="پاسخ خود را بنویسید..."
                />
                            </div>
                        ) : (
                            // گزینه‌ها (برای QType=1 و QType=3)
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 font-public">
                                {Array.isArray(question.Opt) && question.Opt.length > 0 ? (
                                    question.Opt.map((opt: any, index: number) => {
                                        const id = opt?.OID ?? opt?.id;
                                        const isSelected = selectedOptIds.includes(id);
                                        return (
                                            <button
                                                key={id ?? Math.random()}
                                                onClick={() => handleAnswer(id)}
                                                className={`w-full flex items-center justify-center gap-2 p-2 rounded-md border transition-all duration-200 font-medium shadow-sm focus:scale-95 active:scale-95 font-public
                          ${
                                                    isSelected
                                                        ? "border-blue-900 text-white bg-blue-700"
                                                        : "border-blue-900 text-blue-900 bg-white/50 hover:bg-blue-700 hover:text-white"
                                                }`}
                                            >
                                                <span className="font-bold">{index + 1}</span>
                                                {opt?.Title ?? opt?.title ?? "گزینه"}
                                            </button>
                                        );
                                    })
                                ) : (
                                    <div>(گزینه‌ای برای این سوال وجود ندارد)</div>
                                )}
                            </div>
                        )}

                        {/* دکمه بعدی فقط برای QType=2 و QType=3 */}
                        {(isMulti || isText) && (
                            <div className="mt-6">
                                <button
                                    onClick={handleNext}
                                    disabled={isMulti ? selectedOptIds.length === 0 : textAnswer.trim() === ""}
                                    className="px-6 py-2 rounded-md bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed font-public"
                                >
                                    بعدی
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>سؤالی موجود نیست.</div>
                )}
            </div>
        </div>
    );
};

export default Questionnaire;


// import React, { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import { apiFetch } from "../../api/api";
// import { AxiosError } from "axios";
//
// interface Props {
//   formId?: number | string;
//   endpoint?: string;
// }
//
// const Questionnaire: React.FC<Props> = ({ formId: propFormId, endpoint }) => {
//   const params = useParams<{ form_id: string }>();
//   const form_id = propFormId ?? params.form_id ?? null;
//
//   const [question, setQuestion] = useState<any | null>(null);
//   const [loading, setLoading] = useState<boolean>(false);
//   const [finished, setFinished] = useState<boolean>(false);
//   const [error, setError] = useState<string | null>(null);
//
//   const fetchQuestion = async (qid: number | null = null, oid: number | null = null) => {
//     if (form_id == null) return;
//     setLoading(true);
//     setError(null);
//
//     try {
//       const res = await apiFetch({
//         data: { params: [Number(form_id), qid, oid, 10] },
//       });
//       const data = res ?? {};
//       if (data?.success && Array.isArray(data.rows) && data.rows.length > 0) {
//           const q = data.rows[0];
//           if (typeof q.Opt === "string") {
//               try {
//                   q.Opt = JSON.parse(q.Opt);
//               } catch (e) {
//                   q.Opt = [];
//               }
//           }
//         setQuestion(q);
//         setFinished(false);
//       } else {
//         setQuestion(null);
//         setFinished(true);
//       }
//     } catch (e) {
//       const axiosError = e as AxiosError;
//       console.error("خطا در دریافت سوال:", axiosError?.message ?? e);
//       setError(axiosError?.message ?? "خطا در ارتباط با سرور");
//       setQuestion(null);
//       setFinished(true);
//     } finally {
//       setLoading(false);
//     }
//   };
//
//   useEffect(() => {
//     if (form_id != null) fetchQuestion();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [form_id]);
//
//   const handleAnswer = (optId: number | string) => {
//     if (!question) return;
//     fetchQuestion(question.QID ?? null, Number(optId));
//   };
//
//   return (
//     <div
//       dir="rtl"
//       className="flex items-center justify-center min-h-screen bg-center bg-cover"
//       style={{
//         backgroundImage: `url("/hospital.jpeg")`,
//         backgroundSize: "cover",
//         backgroundPosition: "center",
//         filter: "blur(0px)",
//       }}
//     >
//       <div className="text-center text-gray-800 backdrop-blur-[2px]">
//         {loading ? (
//           <div className="text-lg text-gray-700">در حال بارگذاری سوال...</div>
//         ) : error ? (
//           <div className="text-red-600 text-lg font-medium">خطا: {error}</div>
//         ) : finished ? (
//           <div className="text-gray-800 text-lg font-semibold">
//             شما به تمام سوالات پاسخ داده‌اید.
//           </div>
//         ) : question ? (
//           <div>
//             {/* عنوان سوال */}
//             <h2 className="text-lg md:text-xl font-semibold mb-10 font-public">
//               {question.Title ?? "پایان پرسشنامه"}
//             </h2>
//
//             {/* گزینه‌ها */}
//               {/* گزینه‌ها */}
//               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 font-public">
//                   {Array.isArray(question.Opt) && question.Opt.length > 0 ? (
//                       question.Opt.map((opt: any, index: number) => (
//                           <button
//                               key={opt?.OID ?? opt?.id ?? Math.random()}
//                               onClick={() => handleAnswer(opt?.OID ?? opt?.id)}
//                               className="w-full flex items-center justify-center gap-2 p-2 rounded-md border border-blue-900 text-blue-900 bg-white/50 hover:bg-blue-700 hover:text-white transition-all duration-200 font-medium shadow-sm focus:scale-95 active:scale-95 font-public"
//                           >
//                               <span className="font-bold">{index + 1}</span>
//                               {opt?.Title ?? opt?.title ?? "گزینه"}
//                           </button>
//                       ))
//                   ) : (
//                       <div>(گزینه‌ای برای این سوال وجود ندارد)</div>
//                   )}
//               </div>
//
//
//           </div>
//         ) : (
//           <div>سؤالی موجود نیست.</div>
//         )}
//       </div>
//     </div>
//   );
// };
//
// export default Questionnaire;


// import React, { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import { apiFetch } from "../../api/api";
// import { AxiosError } from "axios";

// interface Props {
//   formId?: number | string;
//   endpoint?: string;
// }

// const Questionnaire: React.FC<Props> = ({ formId: propFormId, endpoint }) => {
//   const params = useParams<{ form_id: string }>();
//   const form_id = propFormId ?? params.form_id ?? null;

//   const [question, setQuestion] = useState<any | null>(null);
//   const [loading, setLoading] = useState<boolean>(false);
//   const [finished, setFinished] = useState<boolean>(false);
//   const [error, setError] = useState<string | null>(null);

//   const fetchQuestion = async (qid: number | null = null, oid: number | null = null) => {
//     if (form_id == null) return;
//     setLoading(true);
//     setError(null);

//     try {
//       const res = await apiFetch({
//         data: { params: [Number(form_id), qid, oid, 10] },
//       });

//       const data = res ?? {};
//       if (data?.success && Array.isArray(data.result) && data.result.length > 0) {
//         setQuestion(data.result[0]);
//         setFinished(false);
//       } else {
//         setQuestion(null);
//         setFinished(true);
//       }
//     } catch (e) {
//       const axiosError = e as AxiosError;
//       console.error("خطا در دریافت سوال:", axiosError?.message ?? e);
//       setError(axiosError?.message ?? "خطا در ارتباط با سرور");
//       setQuestion(null);
//       setFinished(true);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (form_id != null) fetchQuestion();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [form_id]);

//   const handleAnswer = (optId: number | string) => {
//     if (!question) return;
//     fetchQuestion(question.QID ?? null, Number(optId));
//   };

//   return (
//     <div
//       dir="rtl"
//       className="flex items-center justify-center min-h-screen bg-cover bg-center"
//       style={{
//         backgroundImage:
//           "assets/images/product/img-19.png",
//         backdropFilter: "blur(10px)",
//       }}
//     >
//       <div className="bg-white/60 backdrop-blur-md shadow-2xl rounded-2xl p-10 w-[600px] text-center border border-white/40">
//         {loading ? (
//           <div className="text-lg text-gray-700">در حال بارگذاری سوال...</div>
//         ) : error ? (
//           <div className="text-red-600 text-lg font-medium">خطا: {error}</div>
//         ) : finished ? (
//           <div className="text-gray-800 text-lg font-semibold">
//             شما به تمام سوالات پاسخ داده‌اید.
//           </div>
//         ) : question ? (
//           <div>
//             <h2 className="text-xl font-semibold text-gray-800 mb-10">
//               {question.Title ?? "بدون عنوان"}
//             </h2>

//             <div className="flex justify-center gap-6">
//               {Array.isArray(question.Opt) && question.Opt.length > 0 ? (
//                 question.Opt.map((opt: any, index: number) => (
//                   <button
//                     key={opt?.OID ?? opt?.id ?? Math.random()}
//                     onClick={() => handleAnswer(opt?.OID ?? opt?.id)}
//                     className="flex items-center justify-center gap-2 w-28 py-2 rounded-xl border border-blue-700 text-blue-900 bg-white/50 hover:bg-blue-700 hover:text-white transition-all duration-300 shadow-md focus:scale-95 active:scale-95"
//                   >
//                     <span className="font-bold text-lg">{index + 1}</span>
//                     {opt?.Title ?? opt?.title ?? "گزینه"}
//                   </button>
//                 ))
//               ) : (
//                 <div>(گزینه‌ای برای این سوال وجود ندارد)</div>
//               )}
//             </div>
//           </div>
//         ) : (
//           <div>سؤالی موجود نیست.</div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default Questionnaire;

// import React, { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import { apiFetch } from "../../api/api";
// import { AxiosError } from "axios";

// interface Props {
//   formId?: number | string;
//   // در صورتی که بخواهی از axios/post مستقیم استفاده کنی میتوانی endpoint را بدهی
//   endpoint?: string;
// }

// const Questionnaire: React.FC<Props> = ({ formId: propFormId, endpoint }) => {
//   const params = useParams<{ form_id: string }>();
//   const form_id = propFormId ?? params.form_id ?? null;

//   const [question, setQuestion] = useState<any | null>(null);
//   const [loading, setLoading] = useState<boolean>(false);
//   const [finished, setFinished] = useState<boolean>(false);
//   const [error, setError] = useState<string | null>(null);

//   const fetchQuestion = async (qid: number | null = null, oid: number | null = null) => {
//     if (form_id == null) return;
//     setLoading(true);
//     setError(null);

//     try {
//       // استفاده از apiFetch پروژه (پِی‌لود مشابه سایر فراخوانی‌ها)
//       const res = await apiFetch({
//         data: { params: [Number(form_id), qid, oid, 10] },
//       });

//       // ایمن‌سازی دسترسی به پاسخ
//       const data = res ?? {};
//       if (data?.success && Array.isArray(data.result) && data.result.length > 0) {
//         setQuestion(data.result[0]);
//         setFinished(false);
//       } else {
//         setQuestion(null);
//         setFinished(true);
//       }
//     } catch (e) {
//       const axiosError = e as AxiosError;
//       console.error("خطا در دریافت سوال:", axiosError?.message ?? e);
//       setError(axiosError?.message ?? "خطا در ارتباط با سرور");
//       setQuestion(null);
//       setFinished(true);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (form_id != null) fetchQuestion();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [form_id]);

//   const handleAnswer = (optId: number | string) => {
//     if (!question) return;
//     // ارسال پاسخ و دریافت سوال بعدی (qid = current QID, oid = انتخاب کاربر)
//     fetchQuestion(question.QID ?? null, Number(optId));
//   };

//   if (loading) return <div>در حال بارگذاری سوال...</div>;
//   if (error) return <div className="text-red-500">خطا: {error}</div>;
//   if (finished) return <div>شما به تمام سوالات پاسخ داده‌اید.</div>;

//   return (
//     <div className="questionnaire">
//       {question ? (
//         <>
//           <h2>{question.Title ?? "بدون عنوان"}</h2>
//           <div className="options">
//             {Array.isArray(question.Opt) && question.Opt.length > 0 ? (
//               question.Opt.map((opt: any) => (
//                 <button
//                   key={opt?.OID ?? opt?.id ?? Math.random()}
//                   onClick={() => handleAnswer(opt?.OID ?? opt?.id)}
//                   className="option-button"
//                 >
//                   {opt?.Title ?? opt?.title ?? "گزینه"}
//                 </button>
//               ))
//             ) : (
//               <div>(گزینه‌ای برای این سوال وجود ندارد)</div>
//             )}
//           </div>
//         </>
//       ) : (
//         <div>سؤالی موجود نیست.</div>
//       )}
//     </div>
//   );
// };

// export default Questionnaire;
